// RECRUITMENT-CANDIDATES-LIST-UI-003 — presentational pieces for the Candidates workspace
import React from 'react';
import { Search, Eye, Pencil, MoreHorizontal, KanbanSquare, UserPlus, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { formatDateOnly } from '../../../utils/formatters';
import { STAGES, StageChip, initialsOf, EmptyState } from './drawer/drawerShared';

const STAGE_ACCENT = {
  applied: 'text-slate-700 bg-slate-100', screening: 'text-blue-700 bg-blue-50', interview: 'text-violet-700 bg-violet-50',
  offer: 'text-amber-700 bg-amber-50', hired: 'text-emerald-700 bg-emerald-50', rejected: 'text-rose-700 bg-rose-50',
};

export const StageSummary = ({ byStage = {}, active, onSelect }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="stage-summary">
    {Object.keys(STAGES).map((s) => {
      const isActive = active === s;
      return (
        <button key={s} type="button" onClick={() => onSelect(isActive ? '' : s)} aria-pressed={isActive} data-testid={`stage-card-${s}`}
          className={`group flex items-center gap-3 rounded-xl border bg-white px-3.5 py-2.5 text-left shadow-sm transition-all hover:-translate-y-px hover:shadow ${isActive ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}>
          <span className={`sf-heading flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg px-2 text-lg font-bold tabular-nums ${STAGE_ACCENT[s]}`} data-testid={`stage-count-${s}`}>{byStage[s] ?? 0}</span>
          <span className="min-w-0">
            <span className="block truncate text-xs font-semibold text-slate-800">{STAGES[s].label}</span>
            <span className="block text-[10px] uppercase tracking-wide text-slate-400">{isActive ? 'Filtering' : 'Stage'}</span>
          </span>
        </button>
      );
    })}
  </div>
);

export const FilterBar = ({ search, onSearch, jobs, job, onJob, stage, onStage, sort, onSort }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2 shadow-sm" data-testid="candidates-filter-bar">
    <div className="relative min-w-[220px] flex-1">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search by name or email…" className="h-9 border-slate-200 pl-9 text-sm" data-testid="candidates-search-input" />
    </div>
    <Select value={job || 'all'} onValueChange={(v) => onJob(v === 'all' ? '' : v)}>
      <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="candidates-job-filter"><SelectValue placeholder="All Jobs" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Jobs</SelectItem>
        {jobs.map((j) => <SelectItem key={j.job_id} value={j.job_id}>{j.title}</SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={stage || 'all'} onValueChange={(v) => onStage(v === 'all' ? '' : v)}>
      <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="candidates-stage-filter"><SelectValue placeholder="All Stages" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Stages</SelectItem>
        {Object.keys(STAGES).map((s) => <SelectItem key={s} value={s}>{STAGES[s].label}</SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={sort} onValueChange={onSort}>
      <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="candidates-sort-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="newest">Newest First</SelectItem>
        <SelectItem value="oldest">Oldest First</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const Row = ({ c, canEdit, onView, onEdit, onPipeline, onConvert }) => (
  <tr className={`group cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-blue-50/40 ${c.is_converted ? 'opacity-70' : ''}`}
    onClick={() => onView(c)} data-testid={`candidate-row-${c.candidate_id}`}>
    <td className="px-4 py-2.5">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[11px] font-bold text-blue-700">{initialsOf(c.name)}</span>
        <span className="min-w-0">
          <span className="flex items-center gap-2"><span className="truncate text-sm font-semibold text-slate-900">{c.name}</span>
            {c.is_converted && <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">Converted</span>}</span>
          <span className="block truncate text-xs text-slate-500"><span className="font-mono text-[11px] text-slate-400">{c.candidate_id}</span> · {c.email}</span>
        </span>
      </div>
    </td>
    <td className="px-4 py-2.5">
      <span className="block truncate text-sm text-slate-800">{c.job_title || c.job_id || '—'}</span>
      {c.job_team && <span className="block text-xs text-slate-400">{c.job_team}</span>}
    </td>
    <td className="px-4 py-2.5"><StageChip stage={c.stage} data-testid={`row-stage-${c.candidate_id}`} /></td>
    <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">{c.created_at ? formatDateOnly(c.created_at) : '—'}</td>
    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-end gap-1">
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-blue-700" onClick={() => onView(c)} title="View candidate" data-testid={`row-view-${c.candidate_id}`}><Eye className="h-4 w-4" /></Button>
        {canEdit && !c.is_converted && (
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-blue-700" onClick={() => onEdit(c)} title="Edit candidate" data-testid={`row-edit-${c.candidate_id}`}><Pencil className="h-4 w-4" /></Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500" title="More" data-testid={`row-more-${c.candidate_id}`}><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onPipeline(c)} className="gap-2" data-testid={`row-pipeline-${c.candidate_id}`}><KanbanSquare className="h-4 w-4" /> Open Pipeline Board</DropdownMenuItem>
            {canEdit && c.stage === 'hired' && !c.is_converted && (
              <DropdownMenuItem onClick={() => onConvert(c)} className="gap-2 text-emerald-700" data-testid={`row-convert-${c.candidate_id}`}><UserPlus className="h-4 w-4" /> Convert to Employee</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </td>
  </tr>
);

export const CandidatesTable = ({ candidates, loading, canEdit, onView, onEdit, onPipeline, onConvert }) => (
  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="candidates-table">
    <table className="w-full">
      <thead>
        <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500">
          <th className="px-4 py-2.5">Candidate</th><th className="px-4 py-2.5">Job</th><th className="px-4 py-2.5">Stage</th><th className="px-4 py-2.5">Applied</th><th className="px-3 py-2.5 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {loading && candidates.length === 0 ? (
          [...Array(5)].map((_, i) => <tr key={i} className="border-b border-slate-100"><td colSpan={5} className="px-4 py-3"><div className="h-8 animate-pulse rounded bg-slate-100" /></td></tr>)
        ) : candidates.length === 0 ? (
          <tr><td colSpan={5} className="p-6"><EmptyState icon={Users} title="No candidates found" hint="Try adjusting the search or filters." testId="candidates-empty" /></td></tr>
        ) : candidates.map((c) => <Row key={c.candidate_id} c={c} canEdit={canEdit} onView={onView} onEdit={onEdit} onPipeline={onPipeline} onConvert={onConvert} />)}
      </tbody>
    </table>
  </div>
);

export const PaginationFooter = ({ page, pages, total, limit, onPage, onLimit }) => {
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const nums = [...new Set([1, page - 1, page, page + 1, pages].filter((n) => n >= 1 && n <= pages))].sort((a, b) => a - b);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600" data-testid="candidates-pagination">
      <span data-testid="candidates-count">Showing <b className="text-slate-900">{from}–{to}</b> of <b className="text-slate-900">{total}</b> candidates</span>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-slate-500">Rows per page
          <Select value={String(limit)} onValueChange={(v) => onLimit(Number(v))}>
            <SelectTrigger className="h-8 w-[72px] text-xs" data-testid="rows-per-page"><SelectValue /></SelectTrigger>
            <SelectContent>{[10, 25, 50].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </label>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-8 gap-1 px-2 text-xs" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="page-prev"><ChevronLeft className="h-3.5 w-3.5" /> Prev</Button>
          {nums.map((n, i) => (
            <React.Fragment key={n}>
              {i > 0 && nums[i - 1] !== n - 1 && <span className="px-1 text-slate-400">…</span>}
              <Button variant={n === page ? 'default' : 'ghost'} size="sm" className="h-8 min-w-[2rem] px-2 text-xs" onClick={() => onPage(n)} data-testid={`page-${n}`}>{n}</Button>
            </React.Fragment>
          ))}
          <Button variant="outline" size="sm" className="h-8 gap-1 px-2 text-xs" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="page-next">Next <ChevronRight className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    </div>
  );
};
